import { compareExpressionsAcrossRolls, type ComparisonVerdict, type FeedbackState, type FormulaVariable , type TriggerFault } from '@agavi/blocks';
import { AlertTriangle, OctagonAlert } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useBrokenRefIssues, useConditionalFormulaRefIssues, useReferencedSourceIssues,
} from './answer-ref-context';
import { revealField } from './field-reveal';
import { useBlockIdentity } from './fired-states';
import { useDrivenLinkIssues } from './use-driven-link-issues';
import { useOutOfRungRecallIssues } from './use-out-of-rung-recall-issues';
import { useOutOfRungVariableIssues } from './use-out-of-rung-variable-issues';

import type { RecallValueSpaces } from './formula-plaintext';
import type { Block, TaskVariable } from '@/types/experience';

import { useCapabilities } from '@/hooks/use-capabilities';


/** A single setup problem for an input block's editor — the shared shape every migrated block's
 * own `get<Block>Issues()` should return, so every block reports and renders warnings/errors the
 * same way: same severity vocabulary, same bold-category-then-detail phrasing, same icon/color
 * treatment, same brand-new-block grace period. The actual checks (which fields are required,
 * what "broken" means) stay in each block's own file — only this shape and its rendering are
 * shared, since those checks genuinely differ block to block. */
export interface BlockIssue {
  key: string;
  severity: 'error' | 'warning';
  /** Which area of the block this points to (e.g. "Question", "Answer Options", "Feedback") —
   * a fixed, per-block vocabulary, rendered in bold ahead of `detail` so a teacher builds one
   * consistent mental map of where to look before even reading what's wrong, rather than parsing
   * a fresh one-off sentence every time. Every block defines its own category set (its areas
   * genuinely differ), but should reuse the same category name for the same concept wherever it
   * recurs across blocks (e.g. "Question", "Feedback") rather than inventing block-specific
   * synonyms for something universal. */
  category: string;
  detail: string;
  /**
   * Somewhere ELSE to go, for an issue whose subject is not in this block.
   *
   * `fields` points at a control here and is the usual case. A recalled answer's warning names
   * a block in another task, which no field id can reach — "you can't act on it" was the
   * owner's complaint, and this is the acting.
   */
  goTo?: () => void;
  /** WHEN this is worth saying, as opposed to `severity`, which is only how loudly it's drawn. The
   * two are independent axes: an incompletion can still be an error once it reaches publication.
   *
   * - `defect` — a CONTRADICTION between things the teacher actually authored (min above max, a
   *   feedback range overlapping the correct band, a log scale whose minimum is zero). Both sides
   *   exist, more typing won't resolve it, and everything downstream is nonsense until it's fixed,
   *   so it surfaces immediately.
   * - `incompletion` — an ABSENCE (blank question, no answer set yet). Nothing is wrong; the block
   *   just isn't finished. Silent while the teacher is working, surfaced once they move on.
   *
   * The test is the same in every block: does this check fire on an absence, or on a conflict? It
   * replaces `isRecentlyCreated`'s 5-minute timer, which applied one arbitrary grace period to
   * every check alike and so made a block you were actively building report finished-block problems.
   *
   * Required. It was optional during the migration, defaulting to `defect` so blocks could convert
   * one at a time with no intermediate state where a real problem went quiet — the noisy default,
   * deliberately. Every check in every block is now tagged, so the default is gone: a new check has
   * to answer the question rather than inherit an answer. */
  /**
   * `unverified` is the third kind: neither a contradiction nor an absence, but "we could not
   * check". It exists for the variable space being too wide to sweep exhaustively — the answers are
   * authored, they may or may not conflict, and the honest report is that nobody knows. Silence
   * there would be indistinguishable from "checked, fine", which is the one reading a teacher must
   * not be given.
   */
  kind: 'defect' | 'incompletion' | 'unverified';
  /** Keys that make this issue redundant. A catch-all ("slider setup is incomplete") names the
   * specific checks in its own area here and steps aside whenever one of them fires, so a teacher
   * is never told the same thing twice at two levels of precision — the single biggest source of
   * the stacking that made these panels cover the editor. */
  supersededBy?: string[];
  /** This gap is already visible, IN PLACE, on the surface that would otherwise summarise it — a
   * blank question renders "No question yet" exactly where the question belongs, under the very
   * same predicate that raises the issue. The collapsed card's one-line status bar therefore skips
   * it and shows the next issue, or the answer.
   *
   * The fourth axis, and the one `supersededBy` doesn't cover: that field means "another ISSUE
   * already says this", while this means "the SURFACE already shows this". Everything else is
   * untouched — the editor's issues panel still lists it (there it costs one row, not the card's
   * only slot for an answer), and publication gating reads the unfiltered set. Read in exactly one
   * place, `collapsedIssueBar`. */
  shownInPlace?: boolean;

  /**
   * Which authored controls this issue is about, as `data-field-id` values.
   *
   * The check already knows: it found the fault in a specific option, item, blank or branch, and
   * most of these keys already carry that id (`option-<id>-unevaluable`). Until now it was thrown
   * away, so every block computed the same fault a SECOND time to decide which box to paint —
   * `optionFormulaIssueIds`, `itemFormulaIssueIds`, `answerErrors` and seventeen more. Two
   * derivations of one fact, kept in step by hand; the panel and the highlight agreeing was a
   * property maintained by discipline rather than by structure.
   *
   * Carrying it means one derivation feeds both, and it is what makes an issue clickable: the
   * panel can focus the control it is talking about.
   *
   * Opaque ids, deliberately. Anything that registers a `data-field-id` is a valid target — an
   * input, an equation field, a branch carousel — so this does not need to know what kind of
   * control it is pointing at.
   */
  fields?: string[];
}

/** Every control the given issues point at. The one derivation a highlight should read. */
export function issueFieldIds(issues: BlockIssue[]): Set<string> {
  const out = new Set<string>();
  for (const i of issues) for (const f of i.fields ?? []) out.add(f);
  return out;
}

/**
 * Moves focus to a control an issue names, and returns whether it found one.
 *
 * Searches the whole document rather than a scoped root: the collapsed card and the editor panel
 * are different subtrees (frequently different component instances), and an issue listed in one
 * can name a field rendered in the other.
 */
function focusFieldElement(id: string): boolean {
  const el = document.querySelector<HTMLElement>(`[data-field-id="${CSS.escape(id)}"]`);
  if (!el) return false;
  // Prefer something the teacher can TYPE in over the first focusable thing in the row. An
  // option row leads with its correct/incorrect toggle, so plain DOM order landed focus on a
  // button when the fault was in the equation beside it.
  const EDITABLE = 'input, textarea, select, [contenteditable="true"]';
  const focusable = el.matches(EDITABLE) ? el
    : el.querySelector<HTMLElement>(EDITABLE)
    ?? (el.matches('button') ? el : el.querySelector<HTMLElement>('button'));
  (focusable ?? el).scrollIntoView({ block: 'center', behavior: 'smooth' });
  (focusable ?? el).focus();
  return true;
}

export function focusIssueField(fields: string[] | undefined): boolean {
  for (const id of fields ?? []) {
    if (focusFieldElement(id)) return true;
    // Not mounted. It may be a page of a carousel that is showing something else — DataViz shows
    // one series at a time, the branching blocks one branch — so ask whoever paginates it to bring
    // that page up, then retry after React has rendered it.
    if (revealField(id)) {
      requestAnimationFrame(() => focusFieldElement(id));
      return true;
    }
  }
  return false;
}

/**
 * Focus a control an issue names, allowing for the surface it lives on to appear first.
 *
 * A collapsed card's issue bar is the case that needs this: the control is not merely on another
 * carousel page, its whole editor is closed, so neither the element NOR its reveal exists at the
 * moment of the click. `onActivate` opens the editor, and the retry runs after React has mounted
 * it — at which point the ordinary reveal-then-focus path applies.
 *
 * Two frames, not one. The first commits the editor; the reveal registered during that commit is
 * what the second frame can then use.
 */
export function focusIssueFieldAfter(fields: string[] | undefined, onActivate?: () => void): void {
  if (focusIssueField(fields)) return;
  if (!onActivate) { warnUnreachable(fields); return; }
  onActivate();
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (!focusIssueField(fields)) warnUnreachable(fields);
  }));
}

/**
 * An issue named a control, `IssueRow` therefore rendered a button, and the button led nowhere.
 *
 * Three blocks shipped in exactly that state — the issue carried the id and nothing registered it.
 * It is invisible from the outside, because a click that does nothing looks the same as a click on
 * the thing you were already looking at, which is why this needs to be noisy in development.
 */
function warnUnreachable(fields: string[] | undefined): void {
  if (import.meta.env.DEV && fields?.length) {
    console.warn(`[block-issues] no data-field-id or reveal for ${fields.join(', ')}`);
  }
}


/** How many issues a panel shows at rest. A cap on HEIGHT, never on access: the remainder stays
 * reachable behind a disclosure, because an unreachable problem is worse than a crowded one. */
/**
 * EVERYTHING A BLOCK'S CHECKS READ BEYOND ITS OWN DATA, as one object.
 *
 * Every block answers `<type>Issues(d, ctx)`, matching `<type>Chips` in `block-condition-chips.tsx`
 * — the same family, named the same way. Before this there were three conventions for one job
 * (`get*Issues` on thirteen blocks, `build*Issues` on three, plus `numericalIssues`) and four
 * parameter shapes, which is what kept the checks reachable only from inside the block that owned
 * them. Neither `get` nor `build` carried information: there is no `get` here that does not build.
 *
 * One object rather than a parameter list because the callers differ in what they HAVE, not in what
 * the checks need: an editor has a recall scope and a resolved asset set, the experience board has
 * neither, and a test has whatever it chose to build. A list makes every caller spell out the
 * absences in order; an object lets them leave out what they do not have.
 */
export interface IssueContext {
  t: (key: string, opts?: Record<string, unknown>) => string;
  /** For the number formatting a few messages do — `fmtLocale`. */
  locale: string;
  /** Task variables PLUS experience variables, the set in force where this block sits. */
  variables: TaskVariable[];
  /**
   * The Variables capability's rung, for the one check that asks whether a variable may be in
   * THIS BLOCK TYPE at all — see `outOfRungVariableIssues`.
   *
   * Optional, and absent means the check does not run. That is the honest default for a caller
   * that cannot know the rung rather than a guess in either direction: inventing a high rung
   * would hide a real defect, and inventing a low one would raise an error against content the
   * teacher is fully entitled to.
   */
  variableRung?: number;
  /** The block's own id, for a check that has to exclude itself from a sweep of its siblings. */
  blockId: string;
  /** Every block in the same task, for a check that looks sideways. */
  blocks: Block[];
  /**
   * What a recall chip in an option could show — see `recallOptionSpaces`. Absent where there is no
   * recall scope, which leaves the collision sweep exactly as it was.
   */
  recalls?: RecallValueSpaces;
  /**
   * GPS targets in OTHER tasks, for the cross-task proximity check. Absent where the caller has not
   * gathered them; the check then reports nothing rather than guessing.
   */
  siblingGpsTargets?: { blockId: string; taskTitle: string; lat: number; lng: number }[];
  /**
   * The editor's LIVE feedback states, which it holds in React state rather than in `d`.
   * Absent elsewhere, where `d`'s own `feedback.states` is the only copy there is.
   */
  feedbackStates?: FeedbackState[];
  /**
   * The blank ids in question order, for FITB. Absent where the caller has not walked the question;
   * the check then walks `d.question` itself, which is the same answer everywhere except an editor
   * holding unsaved question html.
   */
  blankIdsInOrder?: string[];
  /**
   * HOW AN ANSWER FIELD BECOMES A NUMBER, for the one block whose checks need to resolve one.
   *
   * An injection point rather than a draw, because the three callers want three different things:
   * the Slider editor hands in the resolver it already uses against the live roll, a test hands in
   * a stub, and the board has neither. Absent means "evaluate against no values", which is what a
   * freshly opened editor already does.
   *
   * Numerical takes nothing here — it builds its own probe internally (`probeRoll(variables)`),
   * which is the better pattern: an issue check is about the setup and not about today's roll.
   * Converging Slider onto it would change WHICH draw a check evaluates against, which is an audit
   * decision and not a rename.
   */
  resolve?: (s: string, fieldId?: string) => number;
}

/**
 * The part of an issue context every block builds the same way.
 *
 * A block that needs more spreads it: `{ ...issueCtx, recalls }`. One hook rather than an object
 * literal per call site because there are two sites in every block — the collapsed card's memo and
 * the editor's live pass — and two literals kept in step by hand is how they come to disagree.
 */
export function useIssueContext(
  blockId: string, blocks: Block[] | undefined, variables: TaskVariable[] = EMPTY_VARIABLES,
): IssueContext {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const list = blocks ?? EMPTY_BLOCKS;
  /* Read once here rather than by each of the seventeen editors. This hook is the only way an
     editor builds its context, which makes it the one place the rung can enter all of them. */
  const { level } = useCapabilities();
  const variableRung = level('variables');
  return useMemo(
    () => ({ t, locale, variables, blockId, blocks: list, variableRung }),
    [t, locale, variables, blockId, list, variableRung],
  );
}

/** One frozen empty list, so a block with no `blocks` prop does not change the memo every render. */
const EMPTY_BLOCKS: Block[] = [];

/** The same, for the display blocks: Image and Sound have no variables to read. */
const EMPTY_VARIABLES: TaskVariable[] = [];


export const VISIBLE_ISSUE_CAP = 3;

/** Whether the teacher is still working on this block, or has moved on. Deselecting IS the
 * completion boundary — it's the moment they've said "done for now" — and it costs nothing to
 * detect, since leaving the task and opening Preview both deselect too. */
export type AuthoringPhase = 'editing' | 'settled';

/** Drops issues that shouldn't be voiced yet, in one shared pass so no block hand-rolls its own
 * timing rule. Two filters: incompletions stay quiet while the block is being edited, and any
 * catch-all whose more specific sibling is already firing steps aside.
 *
 * Supersession is resolved against the issues that SURVIVED the phase filter, so a catch-all can't
 * be silenced by a specific issue that isn't itself being shown. */
export function applyIssuePolicy(issues: BlockIssue[], phase: AuthoringPhase): BlockIssue[] {
  const voiced = phase === 'settled'
    ? issues
    : issues.filter(i => i.kind !== 'incompletion');
  const present = new Set(voiced.map(i => i.key));
  return voiced.filter(i => !i.supersededBy?.some(k => present.has(k)));
}

/** What a panel actually renders: policy applied, errors ahead of warnings, capped at
 * `VISIBLE_ISSUE_CAP`. `hidden` is returned rather than just a count so the disclosure can list the
 * remainder; `all` is the full post-policy set, which is what `issuesPanelBgClass` must colour from
 * so a hidden error never leaves the panel looking merely amber. */
export function visibleIssues(issues: BlockIssue[], phase: AuthoringPhase): {
  shown: BlockIssue[];
  hidden: BlockIssue[];
  all: BlockIssue[];
} {
  const all = applyIssuePolicy(issues, phase);
  const ranked = [...all].sort((a, b) =>
    (a.severity === 'error' ? 0 : 1) - (b.severity === 'error' ? 0 : 1));
  return { shown: ranked.slice(0, VISIBLE_ISSUE_CAP), hidden: ranked.slice(VISIBLE_ISSUE_CAP), all };
}

/** Was this numeric field actually FILLED IN, as opposed to holding a value? The distinction is
 * load-bearing for every defect check, because the `num()` helpers each block uses for rendering
 * return `0` for an empty string — correct for drawing a track, which needs some position, and
 * wrong for validation, where it silently promotes an untouched field to a real answer of zero.
 * Found live on Slider: a blank answer box was range-checked as an answer of 0 and reported as
 * "outside the slider range," and a blank max made `max - min` negative so every positive step was
 * "larger than the whole range." Two false errors, one missing question. Ask this first. */
export function hasNum(s: string): boolean {
  return s.trim() !== '' && Number.isFinite(parseFloat(s));
}

/** Groups items by exact string equality on whatever key the caller derives, returning only the
 * groups with 2+ members — the raw material for an "identical answer" duplicate check shared
 * across every block whose matching is comparison-based (FITB's free text, scoped per blank by the
 * caller; Keyword's flat keyword list; MCQ/Dropdown's flat option list). Comparison is exact string
 * equality on the caller's own key — deliberately NOT case/accent-normalized or HTML-stripped, so
 * two entries that only look the same after a formatting or normalization difference are never
 * flagged, only entries whose stored content is genuinely identical. `key` returning `''` excludes
 * an item from comparison entirely (blank/unfilled entries would otherwise all "collide" with each
 * other, which isn't a real duplicate — it's just several empty rows). */
export function findDuplicateGroups<T>(items: T[], key: (item: T) => string): T[][] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    if (!k) continue;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(item);
  }
  return Array.from(groups.values()).filter(g => g.length > 1);
}

/** Blank-content guard for `findDuplicateGroups`' own key function — a rich-text field's "empty"
 * state serializes to `<p></p>`, not `''`, so passing raw HTML straight through a bare `.trim()`
 * makes every blank field look like an identical non-empty string, and `findDuplicateGroups`
 * (which only excludes a literal `''` key) then falsely reports them as duplicates of each other.
 * Found live: a block with 2+ blank options showed both "all options are blank" AND "two or more
 * options have identical text: \"\"" at once. Returns `''` (excluded from comparison) for blank
 * content regardless of wrapper markup, or the untouched raw trimmed HTML otherwise — preserving
 * the exact-match, formatting-sensitive comparison every duplicate check already relies on for
 * genuinely non-blank content (two options that only look the same after a bold/italic difference
 * are deliberately NOT flagged). */
export function duplicateKeyIgnoringBlank(html: string): string {
  return html.replace(/<\/?p>/g, '').trim() ? html.trim() : '';
}

/** One consistent look for "a problem statement," used by every surface instead of a hand-rolled
 * shape per location — error and warning are deliberately different icon SILHOUETTES (octagon vs.
 * triangle), not just different colors, so the distinction still reads for a colorblind teacher
 * or in a quick glance. */
export function IssueRow({ issue, size = 'compact', onActivate }: { issue: BlockIssue; size?: 'compact' | 'bar';
  /** Opens the surface the named control lives on, when the click happens with it closed. */
  onActivate?: () => void }) {
  const isError = issue.severity === 'error';
  const tone = isError ? 'text-ws-error-fg' : 'text-ws-warning-fg';
  const bar = size === 'bar';
  const text = bar ? 'text-sm' : 'text-xs';
  const icon = bar ? 'h-4 w-4 shrink-0' : 'mt-0.5 h-3.5 w-3.5 shrink-0';
  const body = (
    <>
      {isError
        ? <OctagonAlert aria-hidden='true' className={icon} />
        : <AlertTriangle aria-hidden='true' className={icon} />}
      <span className='text-left'><strong className='font-semibold'>{issue.category}:</strong> {issue.detail}</span>
    </>
  );

  /**
   * An issue that names a control is a button that goes there. Reading "an option's equation will
   * not evaluate" and then hunting for which option is work the panel can simply do — it already
   * knows, now that the id survives the check.
   *
   * Rendered as a plain div when there is nothing to point at, rather than a dead button: a control
   * that looks interactive and does nothing is worse than one that never offered.
   */
  const align = bar ? 'items-center gap-2' : 'items-start gap-1.5';
  if (!issue.fields?.length && !issue.goTo) {
    return <div className={`flex ${align} ${text} ${tone}`}>{body}</div>;
  }
  return (
    <button
      type='button'
      // `goTo` wins: an issue carrying one is about somewhere else, so focusing a control here
      // would be answering a different question from the one the row asks.
      onClick={() => (issue.goTo ? issue.goTo() : focusIssueFieldAfter(issue.fields, onActivate))}
      className={`flex w-full ${align} rounded ${text} ${tone} hover:underline focus-visible:[outline:2px_auto]`}
    >
      {body}
    </button>
  );
}

/**
 * The full-width tinted strip a DISPLAY block puts under its collapsed card.
 *
 * DataViz, Image and Sound each had a byte-identical private `IssuesBar` — same icons, same
 * severity tones, same `category: detail` shape as `IssueRow`, and none of them clickable. So
 * DataViz could carry an issue naming a series and still give the teacher no way to reach it,
 * because the row it rendered was not the row that knows how to navigate.
 *
 * The input blocks' equivalent is `collapsedIssueBar`; this is the display blocks', and both now
 * end at the same `IssueRow`.
 */
export function IssueBar({ issue, className = '', onActivate }: { issue: BlockIssue; className?: string; onActivate?: () => void }) {
  const bg = issue.severity === 'error' ? 'bg-ws-error-bg' : 'bg-ws-warning-bg';
  return (
    <div className={`px-2 py-2.5 ${bg} ${className}`}>
      <IssueRow issue={issue} size='bar' onActivate={onActivate} />
    </div>
  );
}

/** A flat/neutral surface behind a list of issues reads as inert chrome, the same as any other
 * panel — tinting it to the worst severity present (error red/purple beats warning amber) is what
 * actually pulls a teacher's eye to it as something needing attention, rather than blending in. */
export function issuesPanelBgClass(issues: BlockIssue[]): string {
  return issues.some(i => i.severity === 'error') ? 'bg-ws-error-bg'
    : issues.some(i => i.severity === 'warning') ? 'bg-ws-warning-bg'
    : 'bg-ws-surface';
}

/** The ONE surface that renders a block's problems, replacing the hand-rolled
 * `<div className={issuesPanelBgClass(x)}>{x.map(IssueRow)}</div>` that was copy-pasted to three
 * sites per block across eleven blocks. That duplication is not hypothetical debt: it is exactly
 * how Slider's collapsed card came to recompute its own conditions and paint amber while the editor
 * painted red for the same defect. With one component the card and the editor cannot disagree.
 *
 * `phase` is passed per SITE rather than threaded down from the block list, because each site
 * already knows: the collapsed card only renders when the block is closed, and the editor only
 * renders when it is open. Closing the block IS the completion boundary, so no new state is needed
 * to detect one.
 *
 * The cap governs resting height only — `hidden` stays reachable behind the disclosure, because an
 * unreachable problem is worse than a crowded panel. */
export function IssuesPanel({ issues, phase, className = '' }: {
  issues: BlockIssue[];
  phase: AuthoringPhase;
  className?: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  /*
   * A DEAD REFERENCE is reported here, on the block that holds it, and not by that block.
   *
   * It is the one issue no block can compute: whether a chip in its prose still resolves is a
   * question about the whole experience, and thirty-six call sites each asking it would be
   * thirty-six chances to ask it differently. The hook reads the block's identity from the
   * context it already sits in, so this needs no prop and no block file changes.
   */
  const identity = useBlockIdentity()?.blockId;
  const refIssues = useBrokenRefIssues(identity);
  // The other half: this block is the ANSWER something recalls, and can no longer be read.
  const sourceIssues = useReferencedSourceIssues(identity);
  /* And the third cross-cutting check: this block's versions are chosen by an earlier answer,
     and something about that link is broken. Same arrangement, same reason. */
  const drivenIssues = useDrivenLinkIssues(identity);
  /* And the fourth: a recall this block does ARITHMETIC with is not answered on every route.
     `conditional` is silent everywhere else because prose degrades; a formula does not. */
  const formulaReachIssues = useConditionalFormulaRefIssues(identity);
  /* And the fifth: a variable sits in a block whose rung does not support one. The board found
     this through `issuesForBlock`, which an editor never calls — so the card a teacher had just
     pasted into stayed silent while the error panel named the fault. */
  const rungIssues = useOutOfRungVariableIssues(identity);
  /* And the sixth, its recall twin: a recall in a block whose rung does not support one. */
  const recallRungIssues = useOutOfRungRecallIssues(identity);
  const { shown, hidden, all } = visibleIssues(
    [...issues, ...refIssues, ...sourceIssues, ...drivenIssues, ...formulaReachIssues,
      ...rungIssues, ...recallRungIssues], phase);

  /* Reopening a block starts from the capped view again — an expansion is a momentary "show me the
   * rest", not a preference worth persisting past the visit it was made in. */
  useEffect(() => { if (phase === 'settled') setExpanded(false); }, [phase]);

  if (all.length === 0) return null;
  const rows = expanded ? all : shown;

  return (
    /* Tinted from `all`, never from `rows`: if the only error is one of the hidden ones, a panel
     * coloured from the visible three would sit there amber while the block is actually broken. */
    <div className={`flex flex-col gap-1 ${issuesPanelBgClass(all)} ${className}`}>
      <div id={listId} className='flex flex-col gap-1'>
        {rows.map(issue => <IssueRow key={issue.key} issue={issue} />)}
      </div>
      {hidden.length > 0 && (
        <button
          type='button'
          onClick={() => setExpanded(v => !v)}
          aria-expanded={expanded}
          aria-controls={listId}
          className='self-start rounded text-xs font-semibold text-ws-text-link underline underline-offset-2 hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ws-primary focus-visible:ring-offset-1'>
          {expanded ? t('blocks.issues.showFewer') : t('blocks.issues.showMore', { count: hidden.length })}
        </button>
      )}
    </div>
  );
}

/** Per-block-type cache for `memoizedByKey` — one `WeakMap` shared across every mounted instance
 * of that block type, entries keyed by object identity so unrelated instances never collide. */
export type IssueCache<T> = WeakMap<object, { deps: unknown[]; result: T }>;

/**
 * Skips an expensive-but-pure computation (a block's own `get<Block>Issues`/
 * `compute<Block>TestData`) when neither `key` nor `deps` have changed since the last call —
 * found live via a Firefox profiler trace that a block's own issue-check re-ran on EVERY render
 * of EVERY block in a task, including ones the teacher wasn't even touching, because the parent's
 * `blocks` array is a fresh array reference on every edit even though each UNCHANGED sibling
 * block keeps its own `data` object reference (confirmed in task-workspace-page.tsx's
 * `handleUpdateBlock`: `blocks.map(b => b.id === id ? {...b, data} : b)` — only the edited block
 * gets a new object). None of these block components are wrapped in `React.memo` (a larger,
 * riskier change), so the component function itself still re-runs on every sibling edit — this
 * only skips re-doing the actual expensive work inside it.
 *
 * Deliberately NOT a `useMemo` — `isPreview`/`!isEditing`/open-editor are mutually exclusive
 * early-return branches within the same component, so a hook can't safely live inside just one of
 * them (violates the Rules of Hooks the moment a block toggles between collapsed and open). A
 * plain keyed cache has no such constraint and can be called from any branch.
 *
 * `key` must be the block's OWN stable identity — `block.data` (never the locally-rebuilt
 * `initData(block.data)`/`buildNormalized*Data(...)` object every block's collapsed/preview
 * branch reconstructs fresh on every render regardless of whether the underlying data changed).
 * `deps` covers everything else the computation reads (`variables`, `t`, `block.createdAt`, and
 * for a test-data computation, the block's own live test state) — compared by reference/`===`,
 * matching every other dependency-array convention in this codebase.
 *
 * THE VERSION IN FORCE IS ONE OF THOSE THINGS. A banked block resolves its `d` through a version
 * pointer that is NOT part of `block.data`, so a key of `block.data` alone is the same key for
 * every version in the bank: stepping the test pager moved the pager and left the chips, the
 * answer bar and the issues showing the version before it. Any computation that reads a resolved
 * `d` lists the pointer that resolved it — `shownVersionId` on a card, the drawn version from the
 * task roll in a preview.
 */
export function memoizedByKey<T>(cache: IssueCache<T>, key: object, deps: unknown[], compute: () => T): T {
  const cached = cache.get(key);
  if (cached && cached.deps.length === deps.length && cached.deps.every((d, i) => d === deps[i])) {
    return cached.result;
  }
  const result = compute();
  cache.set(key, { deps, result });
  return result;
}

/**
 * The message a CARD shows for a trigger fault.
 *
 * Deliberately shorter than the wording the issues BAR uses for the same fault. The bar is remote
 * from the thing it describes, so it has to say "a targeted state's formula does not resolve" — the
 * card IS the state, and repeating that on it reads as a form letter. No full stop either, matching
 * every other note a card carries ("Name already in use", "No feedback text").
 *
 * One function rather than one switch per block: four blocks report the same four faults, and four
 * copies of the mapping is how three of them would end up saying it slightly differently.
 */
export function triggerFaultMessage(fault: TriggerFault, t: (k: string) => string): string {
  switch (fault) {
    case 'unresolvable':     return t('blocks.conditions.faultUnresolvable');
    case 'range-incomplete': return t('blocks.conditions.faultRangeIncomplete');
    case 'no-value':         return t('blocks.conditions.faultNoValue');
    case 'range-inverted':   return t('blocks.conditions.betweenInverted');
  }
}

// ─── Inverted ranges, quantified ─────────────────────────────────────────────

/**
 * Whether any `between` row's bounds come out the wrong way round, across every value the
 * variables can take.
 *
 * Four blocks asked this and all four answered from a single roll, each differently wrong:
 *
 *   numerical  evaluated at the PROBE roll — each variable's minimum, which is the roll least
 *              likely to be inverted, so it under-reported almost by construction
 *   slider     skipped formula bounds outright (`!hasVariableChip(...)`), so it never fired for the
 *              only case that needs it
 *   angle      used `num()`, which returns 0 for a formula, so `{a} > {b}` was `0 > 0` and the
 *              check could not fire even in principle
 *   compass    had no check at all
 *
 * Returns the WORST verdict across the rows, since one broken row is enough: `always` is a defect
 * (that row accepts nothing, ever), `sometimes` is a warning (it accepts nothing on some rolls),
 * `unknown` means the variable space was too wide to sweep and is reported rather than hidden.
 *
 * Deliberately NOT a swap. Reordering the bounds would change which question is graded without
 * telling the author, and with variables they could not see which way round any given student got
 * it — see the standing decision on this.
 */
export function betweenInversionVerdict(
  /** `angleMode` per row, because a between-answer's bounds are a FIELD and each field has its
   *  own mode. The parameter below is only the fallback for a row that names none. */
  rows: ReadonlyArray<{ low: string; high: string; angleMode?: 'degrees' | 'radians' }>,
  variables: FormulaVariable[],
  angleMode: 'degrees' | 'radians' = 'radians',
): ComparisonVerdict {
  const rank: Record<ComparisonVerdict, number> = { never: 0, unknown: 1, sometimes: 2, always: 3 };
  let worst: ComparisonVerdict = 'never';
  for (const r of rows) {
    // A half-typed row is not a defect, it is a row still being written. Its absence is reported by
    // the blank-field checks, which is the one place it belongs.
    if (r.low.trim() === '' || r.high.trim() === '') continue;
    const v = compareExpressionsAcrossRolls(r.low, r.high, variables, r.angleMode ?? angleMode);
    if (rank[v] > rank[worst]) worst = v;
  }
  return worst;
}

/**
 * Whether any targeted feedback `range` trigger has its bounds the wrong way round.
 *
 * Read from the RAW trigger, before normalization, which is the whole trick. Every block's
 * evaluator normalizes a range trigger with `Math.min`/`Math.max` (13 sites across five blocks), so
 * by the time any accepted-range maths sees it the inversion is gone. I had planned to remove that
 * swap so inversion could be detected — wrong, and it would have been a grading change for
 * already-authored content: the swap is what makes an inverted trigger still match its intended
 * band. Keeping it and checking the raw values gives the teacher the warning while nothing about
 * grading moves.
 *
 * The consequence is milder than an inverted ANSWER, and the severity reflects that: an inverted
 * answer band means a question nobody can get right, while an inverted feedback band just means
 * that state never fires and the student falls through to Default Incorrect — still graded
 * correctly, only less specific. So `always` here is a warning, not an error, and the blocks'
 * existing reachability machinery is what says "this state cannot fire".
 */
export function triggerRangeInversionVerdict(
  triggers: readonly string[],
  variables: FormulaVariable[],
  angleMode: 'degrees' | 'radians' = 'radians',
  /** Each trigger's OWN mode, by its index in `triggers` — a feedback trigger is a field and
   *  carries one. Omitted, every trigger falls back to `angleMode`. */
  modeOf?: (index: number) => 'degrees' | 'radians' | undefined,
): ComparisonVerdict {
  const rows: { low: string; high: string; angleMode?: 'degrees' | 'radians' }[] = [];
  for (const [i, trig] of triggers.entries()) {
    // `type:v1:v2`, shared by every block with range triggers. Only `range` has two bounds.
    const parts = trig.split(':');
    const bare = /^[ab]:/.test(trig) ? trig.slice(2).split(':') : parts;   // slider prefixes a handle
    if (bare[0] !== 'range') continue;
    const low = bare[1] ?? '', high = bare[2] ?? '';
    // LITERAL bounds are skipped, and that is not a shortcut. Each block already checks its own
    // literal inversions with the domain knowledge needed to judge them: compass passes
    // `allowWrap: true` because a band from 350 to 10 runs through north and is the ORDINARY case
    // there, while on a slider the same pair accepts nothing. Judging literals here, with no such
    // knowledge, would fire a spurious warning on every compass band crossing the seam.
    //
    // Equations are what no block could check, because `num()` flattens them to 0 — so those are
    // exactly what this adds, and the double-reporting is avoided by construction.
    if (!low.includes('{') && !high.includes('{')) continue;
    rows.push({ low, high, angleMode: modeOf?.(i) });
  }
  return betweenInversionVerdict(rows, variables, angleMode);
}

/**
 * `between` rows with exactly one bound filled — the authoring mistake that grades silently.
 *
 * `computeAcceptedRanges` DROPS a row unless both bounds are finite, in every one of the four
 * value-range blocks, so a half-filled row contributes nothing to what is accepted. That is the
 * right behaviour (guessing the missing bound as +/-infinity would fail OPEN and accept nearly
 * everything), but it is invisible: if the row is the only one, nothing a student types can be
 * correct, and if it sits beside a complete row, the range the teacher meant to add simply does
 * not exist and students who give it are marked wrong.
 *
 * Numerical already reported this, because its blank-field check looks at every bound of every row.
 * Slider, angle and compass gated on `!betweenAnswers.some(both bounds set)` — "setup incomplete"
 * — which one complete row satisfies, so a half-filled row BESIDE a complete one was silent on all
 * three. That is the likelier case in practice: a teacher adds a second row, fills one bound, and
 * moves on.
 *
 * Returns the ids of the offending rows, so the issue can ring the fields as well as name the
 * problem. An INCOMPLETION rather than a defect: nothing contradicts anything, a value is simply
 * missing, so it belongs at a completion boundary rather than interrupting mid-typing.
 */
export function incompleteBetweenRowIds(
  rows: ReadonlyArray<{ id?: string; low: string; high: string }>,
): string[] {
  return rows
    .filter(r => {
      const lo = r.low.trim() !== '', hi = r.high.trim() !== '';
      return lo !== hi;                      // exactly one side filled
    })
    .map(r => String(r.id ?? ''))
    .filter(Boolean);
}
